/**
 * The profile document, `cellars/{cellarId}/profile.json` (data-model.md "Profile document"):
 * the person's email for the monthly digest, an optional display name for the owner's cellar
 * selector (FR-040), the digest preference (FR-034) and, from version 2, the person's language
 * (FR-035, T076b).
 */

/** Written into every profile. Bump only with a migration (CLAUDE.md data safety rule 3). */
export const PROFILE_SCHEMA_VERSION = 2;

/** The app's languages (constitution 1.2.0). French is the default. */
export type Language = 'fr' | 'en';
export const DEFAULT_LANGUAGE: Language = 'fr';

export interface Profile {
  schemaVersion: number;
  email: string;
  /** Optional; the email is shown when there is none. */
  name?: string;
  digestEnabled: boolean;
  /** The person's language for screens and emails (version 2). */
  language: Language;
}

/** Version 1, as written before T076b: no language (so French). Kept for reading old documents. */
interface ProfileV1 {
  schemaVersion: 1;
  email: string;
  name?: string;
  digestEnabled: boolean;
}

/** The stored profile is newer than this code: it must not be read, and so never overwritten. */
export class ProfileVersionTooNewError extends Error {
  constructor(public readonly schemaVersion: number) {
    super(`profile.json version ${schemaVersion} is newer than this app understands.`);
    this.name = 'ProfileVersionTooNewError';
  }
}

/** The profile written at a person's first sign-in: no name, monthly email on, French. */
export function createProfile(email: string): Profile {
  return {
    schemaVersion: PROFILE_SCHEMA_VERSION,
    email: email.trim().toLowerCase(),
    digestEnabled: true,
    language: DEFAULT_LANGUAGE,
  };
}

/** Version 1 → 2: add the language, French, as everyone used the app in French until then. */
export function migrateProfileV1ToV2(v1: ProfileV1): Profile {
  return { ...v1, schemaVersion: 2, language: DEFAULT_LANGUAGE };
}

const isLanguage = (value: unknown): value is Language => value === 'fr' || value === 'en';

/**
 * Reads a stored profile of any version this code knows, as the current version, without changing
 * the stored object. The upgraded shape is only written back the next time the profile is saved.
 * Throws ProfileVersionTooNewError for a newer version, and an Error for anything that is not a
 * profile.
 */
export function migrateProfile(stored: unknown): Profile {
  const doc = stored as Partial<ProfileV1 & Profile> | null;
  if (
    typeof doc !== 'object' ||
    doc === null ||
    typeof doc.email !== 'string' ||
    typeof doc.digestEnabled !== 'boolean' ||
    typeof doc.schemaVersion !== 'number'
  ) {
    throw new Error('Not a profile document.');
  }
  if (doc.schemaVersion > PROFILE_SCHEMA_VERSION) {
    throw new ProfileVersionTooNewError(doc.schemaVersion);
  }

  const base: ProfileV1 = {
    schemaVersion: 1,
    email: doc.email,
    ...(doc.name !== undefined ? { name: doc.name } : {}),
    digestEnabled: doc.digestEnabled,
  };
  const current = migrateProfileV1ToV2(base);
  // A version 2 profile keeps its language; an unknown value reads as French rather than failing.
  return doc.schemaVersion >= 2 && isLanguage(doc.language)
    ? { ...current, language: doc.language }
    : current;
}

/** The name shown for a cellar: the profile's name, or its email when there is none. */
export function profileDisplayName(profile: Profile): string {
  return profile.name?.trim() || profile.email;
}

/** The longest cellar name accepted (owner request, 2026-10-08). */
export const MAX_CELLAR_NAME_LENGTH = 60;

/** Thrown for a cellar name that cannot be saved. */
export class InvalidCellarNameError extends Error {
  constructor() {
    super('Nom de cave invalide.');
    this.name = 'InvalidCellarNameError';
  }
}

/**
 * The cellar's own name (owner request, 2026-10-08), set under Réglages and shown in the top bar
 * and the owner's "Changer de cave" list; undefined when the person has not named it.
 */
export function cellarName(profile: Profile): string | undefined {
  return profile.name?.trim() || undefined;
}

/**
 * The profile with its cellar named `name`: trimmed, inner spaces collapsed; an empty name removes
 * it so the default name ("Ma cave", "Cave de {email}") shows again. At most 60 characters.
 */
export function withCellarName(profile: Profile, name: string): Profile {
  const tidy = name.trim().replace(/\s+/g, ' ');
  if (tidy.length > MAX_CELLAR_NAME_LENGTH) throw new InvalidCellarNameError();
  const { name: _previous, ...rest } = profile;
  return tidy === '' ? rest : { ...rest, name: tidy };
}
